import { supabase } from './supabase/client';
import { PermissionKey, RoleDefinition, RoleShell } from 'src/types/permissions.types';

const TABLE_NAME = 'roles';
const ROLE_NAME_PATTERN = /^[A-Z][A-Z0-9_]*$/;

const TOO_MUCH_ACCESS = 'You cannot change a role with more access than your own.';

/** The database refuses role changes above the caller's own access: 42501 = blocked write, PGRST116 = row hidden. */
function explain(error: any) {
  return error?.code === '42501' || error?.code === 'PGRST116' ? new Error(TOO_MUCH_ACCESS) : error;
}

export type RoleInput = Omit<RoleDefinition, 'is_system'>;
export interface RoleMember {
  id: string;
  firstname: string | null;
  lastname: string | null;
  email: string;
  role: string | null;
}

export type RoleUpdate = Partial<Pick<RoleDefinition, 'label' | 'description' | 'base_route' | 'permissions'>>;

/** Turns a display name into a role key: 'Vice Principal' => 'VICE_PRINCIPAL'. */
export function toRoleName(label: string): string {
  return label
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function toDefinition(row: any): RoleDefinition {
  return {
    name: row.name,
    label: row.label || row.name,
    description: row.description,
    base_route: row.base_route as RoleShell,
    permissions: Array.isArray(row.permissions) ? (row.permissions as PermissionKey[]) : [],
    is_system: Boolean(row.is_system),
  };
}

export const roleService = {
  async getRoles(): Promise<RoleDefinition[]> {
    const { data, error } = await supabase.from(TABLE_NAME).select('*').order('name');
    if (error) throw error;
    return (data || []).map(toDefinition);
  },

  async createRole(role: RoleInput): Promise<RoleDefinition> {
    const name = toRoleName(role.name);
    if (!ROLE_NAME_PATTERN.test(name)) {
      throw new Error('Role name must start with a letter and use only letters, numbers and underscores.');
    }
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .insert({
        name,
        label: role.label.trim() || name,
        description: role.description || null,
        base_route: role.base_route,
        permissions: role.permissions,
        is_system: false,
      })
      .select()
      .single();
    if (error) throw explain(error);
    return toDefinition(data);
  },

  async updateRole(name: string, patch: RoleUpdate): Promise<RoleDefinition> {
    const { data, error } = await supabase.from(TABLE_NAME).update(patch).eq('name', name).select().single();
    if (error) throw explain(error);
    return toDefinition(data);
  },

  /** Deletes a custom role. Refuses while any user still holds it. */
  async deleteRole(role: RoleDefinition): Promise<void> {
    if (role.is_system) throw new Error('Built-in roles cannot be deleted.');
    const { count, error: countError } = await supabase
      .from('users')
      .select('id', { count: 'exact', head: true })
      .eq('role', role.name)
      .is('deletedat', null);
    if (countError) throw countError;
    if (count && count > 0) {
      throw new Error(`${count} user(s) still have the ${role.label} role. Reassign them first.`);
    }
    // RLS hides roles above the caller's access, so a refused delete removes nothing and returns no error.
    const { data, error } = await supabase.from(TABLE_NAME).delete().eq('name', role.name).select('name');
    if (error) throw explain(error);
    if (!data?.length) throw new Error(TOO_MUCH_ACCESS);
  },

  /** Everyone who can sign in, with their current role. Drives member lists and pickers. */
  async getMembers(): Promise<RoleMember[]> {
    // ponytail: loads every user (~500 today); page this if the school grows past a few thousand.
    const { data, error } = await supabase
      .from('users')
      .select('id, firstname, lastname, email, role')
      .is('deletedat', null)
      .order('firstname')
      .range(0, 9999);
    if (error) throw error;
    return (data || []) as RoleMember[];
  },

  /**
   * Gives a user a role. A Banker works through their own cohort
   * enrollments, so that role needs a scholar account.
   */
  async assignRole(userId: string, roleName: string): Promise<RoleMember> {
    if (roleName === 'BANKER') {
      const { data: student } = await supabase.from('students').select('id').eq('userid', userId).maybeSingle();
      if (!student) throw new Error('Only a scholar account can be a Student Banker.');
    }

    const { data, error } = await supabase
      .from('users')
      .update({ role: roleName })
      .eq('id', userId)
      .select('id, firstname, lastname, email, role')
      .single();
    if (error) throw explain(error);
    return data as RoleMember;
  },
};

export default roleService;
